import { type FC, useMemo } from 'react';
import { SearcherDocumentSearchFilterOperator } from 'src/api/generated';
import Select from 'src/modules/shared/components/atoms/inputs/select';
import SearchableSelect from 'src/modules/shared/components/atoms/inputs/searchable-select';
import { type FilterFieldOptions } from 'src/modules/shared/types/filters.types';
import { FIELD_TYPE_OPERATORS } from 'src/modules/shared/utils/filters/operators.util';

export interface FilterConditionProps {
  fieldsOptions: FilterFieldOptions[];
  selectedFieldId?: string;
  selectedOperator?: SearcherDocumentSearchFilterOperator;
  onFieldChange: (fieldId: string) => void;
  onOperatorChange: (operator: SearcherDocumentSearchFilterOperator) => void;
}

export const FilterCondition: FC<FilterConditionProps> = ({
  fieldsOptions,
  selectedFieldId,
  selectedOperator,
  onFieldChange,
  onOperatorChange,
}) => {
  const fieldOptionsOptions = useMemo(() => {
    return fieldsOptions.map((field) => ({
      content: field.label,
      value: field.id,
    }));
  }, [fieldsOptions]);

  const selectedField = useMemo(() => {
    return fieldsOptions.find((field) => field.id === selectedFieldId);
  }, [fieldsOptions, selectedFieldId]);

  const selectedFieldType = useMemo(() => {
    return selectedField?.type ?? 'string';
  }, [selectedField]);

  const operatorOptions = useMemo(() => {
    // Special handling for archivedStatus - only show "equals" operator
    // TODO: make this more generic
    if (selectedFieldId === 'archivedStatus') {
      return [
        {
          content: 'es',
          value: SearcherDocumentSearchFilterOperator.EQ,
        },
      ];
    }

    const operators = FIELD_TYPE_OPERATORS[selectedFieldType];

    return operators.map((op) => ({
      content: op.label,
      value: op.value,
    }));
  }, [selectedFieldType, selectedFieldId]);

  return (
    <div className="flex w-full flex-col items-stretch gap-2 sm:col-span-3 sm:grid sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] sm:items-center">
      <SearchableSelect
        options={fieldOptionsOptions}
        size="sm"
        variant="primary"
        value={selectedFieldId}
        onChange={(val) => val && onFieldChange(val)}
        // TODO: check when implementing i18n
        placeholder="Selecciona o busca..."
        containerClassName="w-full sm:min-w-0"
      />
      <Select
        options={operatorOptions}
        size="sm"
        value={selectedOperator}
        onChange={(e) =>
          onOperatorChange(
            e.target.value as SearcherDocumentSearchFilterOperator
          )
        }
        disabled={!selectedFieldId}
        className="w-full"
        containerClassName="w-full sm:min-w-0"
      />
    </div>
  );
};
